import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@mui/material';
import {
  Email as EmailIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  Event as EventIcon,
  Close as CloseIcon,
  ContentPaste as PasteIcon,
  CheckCircle as CheckIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

const categoryIcons = {
  transport: <FlightIcon color="primary" />,
  hotel: <HotelIcon sx={{ color: '#e65100' }} />,
  restaurant: <RestaurantIcon sx={{ color: '#2e7d32' }} />,
  event: <EventIcon sx={{ color: '#7b1fa2' }} />,
  activity: <EventIcon sx={{ color: '#1565c0' }} />,
  other: <CategoryIcon color="action" />,
};

const categoryLabels = {
  transport: 'Flight / Transport',
  hotel: 'Hotel / Accommodation',
  restaurant: 'Restaurant',
  event: 'Event / Activity',
  activity: 'Activity',
  other: 'Other',
};

const EmailImportDialog = ({ open, onClose, tripId, onImportComplete }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [emailContent, setEmailContent] = useState('');
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [selectedReservations, setSelectedReservations] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const steps = [
    t('emailImport.stepPaste'),
    t('emailImport.stepReview'),
    t('emailImport.stepDone')
  ];

  const handleParse = async () => {
    if (!emailContent.trim()) {
      setError(t('emailImport.emptyContent'));
      return;
    }

    setParsing(true);
    setError('');

    try {
      const response = await api.post('/email-import/parse', {
        content: emailContent,
        tripId
      });

      if (response.data.success && response.data.reservations.length > 0) {
        setReservations(response.data.reservations);
        setSelectedReservations(response.data.reservations.map((_, i) => i));
        setActiveStep(1);
      } else {
        setError(t('emailImport.noReservationsFound'));
      }
    } catch (err) {
      console.error('Parse error:', err);
      setError(err.response?.data?.error || t('emailImport.parseError'));
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    const toImport = selectedReservations.map(idx => reservations[idx]);
    
    if (toImport.length === 0) {
      setError(t('emailImport.selectAtLeastOne'));
      return;
    }

    setImporting(true);
    setError('');

    try {
      const response = await api.post('/email-import/import', {
        tripId,
        reservations: toImport
      });

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setActiveStep(2);
        
        // Trigger refresh after short delay
        setTimeout(() => {
          if (onImportComplete) onImportComplete();
        }, 500);
      }
    } catch (err) {
      console.error('Import error:', err);
      setError(err.response?.data?.error || t('emailImport.importError'));
    } finally {
      setImporting(false);
    }
  };

  const handleToggleReservation = (index) => {
    setSelectedReservations(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setEmailContent(text);
    } catch (err) {
      console.warn('Clipboard access denied');
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setEmailContent('');
    setReservations([]);
    setSelectedReservations([]);
    setError('');
    setSuccessMessage('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('emailImport.title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Step 1: Paste Email */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('emailImport.pasteInstructions')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('emailImport.orForward', {
                address: t('emailForward.defaultAddress', 'plans@in.dejavunomad.com'),
              })}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                size="small"
                startIcon={<PasteIcon />}
                onClick={handlePasteFromClipboard}
                sx={{ textTransform: 'none' }}
              >
                {t('emailImport.pasteClipboard')}
              </Button>
            </Box>

            <TextField
              multiline
              rows={10}
              fullWidth
              placeholder={t('emailImport.placeholder')}
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                }
              }}
            />

            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
              {t('emailImport.supportedTypes')}
            </Typography>
          </Box>
        )}

        {/* Step 2: Review Detected Reservations */}
        {activeStep === 1 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              {t('emailImport.foundReservations', { count: reservations.length })}
            </Alert>

            <List sx={{ bgcolor: 'grey.50', borderRadius: 2 }}>
              {reservations.map((reservation, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{ px: 2 }}
                    secondaryAction={
                      <Checkbox
                        checked={selectedReservations.includes(index)}
                        onChange={() => handleToggleReservation(index)}
                        color="primary"
                      />
                    }
                  >
                    <ListItemIcon>
                      {categoryIcons[reservation.category] || categoryIcons.other}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {reservation.name}
                          </Typography>
                          <Chip 
                            label={categoryLabels[reservation.category] || reservation.category}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {reservation.location && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              📍 {reservation.location}
                            </Typography>
                          )}
                          {reservation.startDatetime && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              📅 {new Date(reservation.startDatetime).toLocaleDateString()}
                              {reservation.endDatetime && ` → ${new Date(reservation.endDatetime).toLocaleDateString()}`}
                            </Typography>
                          )}
                          {reservation.confirmationNumber && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              🔑 {t('emailImport.confirmation')}: {reservation.confirmationNumber}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < reservations.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}

        {/* Step 3: Success */}
        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('emailImport.success')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {successMessage}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleParse}
              disabled={parsing || !emailContent.trim()}
              startIcon={parsing ? <CircularProgress size={16} /> : <EmailIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {parsing ? t('emailImport.parsing') : t('emailImport.detectReservations')}
            </Button>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)} sx={{ textTransform: 'none' }}>
              {t('common.back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing || selectedReservations.length === 0}
              startIcon={importing ? <CircularProgress size={16} /> : <CheckIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {importing 
                ? t('emailImport.importing')
                : t('emailImport.importSelected', { count: selectedReservations.length })
              }
            </Button>
          </>
        )}

        {activeStep === 2 && (
          <Button 
            variant="contained" 
            onClick={handleClose}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            {t('common.done')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmailImportDialog;
